import { Label, Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@creanizer/ui'
import { Info } from 'lucide-react'
import type { WorldBuilderInput, WorldTemplate } from '@creanizer/types'
import { WORLD_TEMPLATES, TEMPLATE_CATEGORIES } from '../constants/templates'

interface TemplateModeFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

export function TemplateModeForm({ input, onChange }: TemplateModeFormProps) {
  const handleTemplateSelect = (templateId: WorldTemplate) => {
    const template = WORLD_TEMPLATES.find(t => t.id === templateId)
    if (!template) return
    
    const defaultParams = {
      magicLevel: 5,
      scienceLevel: 5,
      civilizationLevel: 5,
      dangerLevel: 5,
      mysteryLevel: 5,
      complexityLevel: 5,
      uniquenessLevel: 5,
      chaosLevel: 5,
    }
    
    onChange({
      ...input,
      templateType: templateId,
      parameters: {
        ...defaultParams,
        ...template.defaultParameters,
      },
    })
  }
  
  const selectedTemplate = WORLD_TEMPLATES.find(t => t.id === input.templateType)
  
  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          テンプレートを選ぶと、特殊な世界構造を持つ世界観を生成できます。
          浮遊島、地下文明、時間ループなど、ユニークな舞台設定が作られます。
        </AlertDescription>
      </Alert>
      
      <div>
        <Label>テンプレートを選択</Label>
        <Accordion type="single" collapsible className="mt-2">
          {TEMPLATE_CATEGORIES.map((category) => (
            <AccordionItem key={category.id} value={category.id}>
              <AccordionTrigger className="text-sm">
                <div className="text-left">
                  <div className="font-semibold">{category.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {category.description}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-3 p-2">
                  {category.templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`
                        p-3 text-left border rounded-lg transition-colors
                        ${input.templateType === template.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-accent'
                        }
                      `}
                    >
                      <div className="font-semibold text-sm">{template.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {template.description}
                      </div>
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      
      {selectedTemplate && (
        <div className="space-y-2">
          <Label>テンプレート情報</Label>
          <div className="text-sm p-3 bg-muted rounded-lg">
            <div className="font-medium mb-1">{selectedTemplate.name}</div>
            <div className="text-muted-foreground">
              {selectedTemplate.description}
            </div>
            {selectedTemplate.defaultParameters && (
              <div className="mt-2 space-y-1">
                {selectedTemplate.defaultParameters.magicLevel !== undefined && (
                  <div className="text-xs">
                    推奨魔法レベル: {selectedTemplate.defaultParameters.magicLevel}/10
                  </div>
                )}
                {selectedTemplate.defaultParameters.scienceLevel !== undefined && (
                  <div className="text-xs">
                    推奨科学レベル: {selectedTemplate.defaultParameters.scienceLevel}/10
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div>
        <Label htmlFor="templateModifiers">テンプレート修飾（オプション）</Label>
        <Textarea
          id="templateModifiers"
          placeholder="テンプレートに追加したい要素や変更点を入力..."
          value={input.templateModifiers?.join('\n') || ''}
          onChange={(e) => onChange({
            ...input,
            templateModifiers: e.target.value.split('\n').filter(Boolean)
          })}
          rows={3}
        />
      </div>
    </div>
  )
}